import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ODAViewer } from "../components/cad/ODAViewer";
import { useProject, useRun, useRunLayout } from "../hooks/useProjects";
import { MOCK_LAYOUT } from "../lib/mockLayout";
import type { Layout } from "../types/layout";
import type { RunLayout } from "../types/projects";

// Locked Stage-9 packer constants (mirrors app/services/layout/pack.py).
// pitch_x is derived from gcr: pitch_x = tracker_width / gcr.
const DEFAULT_GCR = 0.3;
const PACKING_DEFAULTS = {
  tracker_width: 2.278,
  tracker_length: 129.668,
  pitch_x: 2.278 / DEFAULT_GCR,
  gap_y: 0.5,
  gcr: DEFAULT_GCR,
  modules_per_tracker: 108,
} as const;

function normalisePlacementOffsets(
  raw: RunLayout["placement_offset"],
): Record<string, { x: number; y: number }> {
  // Stage 9 wire-shape: a flat {x, y}. Normalise to the Stage 10 map
  // so the viewer never branches on shape. The runtime shape check
  // uses typeof + 'x' presence; TS can't narrow the union over a
  // discriminator that isn't a literal tag, so cast through unknown
  // once we've established the shape.
  const flat = raw as unknown as { x?: unknown; y?: unknown };
  if (typeof flat.x === "number" && typeof flat.y === "number") {
    return { "108_INT": { x: flat.x, y: flat.y } };
  }
  return { ...(raw as Record<string, { x: number; y: number }>) };
}

function modulesForBlock(block: string): number {
  if (block === "81_EPN") return 81;
  return PACKING_DEFAULTS.modules_per_tracker;
}

function wireToLayout(wire: RunLayout): Layout {
  const isAc = wire.study_type === "ac_block_layout";
  return {
    packing_config: { ...PACKING_DEFAULTS },
    boundary: {
      points: wire.boundary.map(([x, y]) => ({ x, y, z: 0 })),
      closed: true,
    },
    ...(wire.boundaries
      ? {
          boundaries: wire.boundaries.map((ring) => ({
            points: ring.map(([x, y]) => ({ x, y, z: 0 })),
            closed: true,
          })),
        }
      : {}),
    ...(wire.site_features
      ? { siteFeatures: wire.site_features }
      : {}),
    trackers: wire.trackers.map((t) => {
      const block = t.block ?? "108_INT";
      return {
        id: t.id,
        sw_x: t.sw_x,
        sw_y: t.sw_y,
        modules_count: modulesForBlock(block),
        block,
      };
    }),
    placement_offset: normalisePlacementOffsets(wire.placement_offset),
    ...(isAc ? { study_type: "ac_block_layout" as const } : {}),
    ...(wire.inverters ? { inverters: wire.inverters.map((i) => ({ ...i })) } : {}),
    ...(wire.roads
      ? {
          roads: wire.roads.map((r) => ({
            type: r.type,
            poly: r.poly.map(([x, y]) => ({ x, y })),
          })),
        }
      : {}),
    ...(wire.ac_block_params
      ? { ac_block_params: { ...wire.ac_block_params } }
      : {}),
    ...(wire.packing_report
      ? { packing_report: { ...wire.packing_report } }
      : {}),
  };
}

export function LayoutPage() {
  const { projectId, runId } = useParams<{
    projectId: string;
    runId: string;
  }>();
  const [layout, setLayout] = useState<Layout | null>(null);
  const [saveRequestId, setSaveRequestId] = useState<number>(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // Delete: re-enabled (render-on-demand + regenAll experiment).
  const [deleteRequestId, setDeleteRequestId] = useState<number>(0);
  // Manual "Regen" — force an in-place re-render of the viewer.
  const [regenRequestId, setRegenRequestId] = useState<number>(0);

  // Names for the breadcrumb + back-link label. Both queries dedupe with
  // whatever the Studies tab already fetched, so this is usually instant.
  const { data: project } = useProject(projectId);
  const { data: run } = useRun(runId === "demo" ? undefined : runId);
  const { data: runLayout, error: runLayoutError } = useRunLayout(
    runId === "demo" ? undefined : runId,
  );
  const projectName = project?.name ?? null;
  const study =
    run && project ? project.studies.find((s) => s.id === run.study_id) : null;
  const studyName = study?.name ?? null;
  const runLabel = run ? `Run #${run.run_number}` : null;

  // Demo route gets the static mock; real runs read through React Query.
  // On fetch failure we mirror the previous behaviour (fall back to mock,
  // log the error) so the viewer still has something to render.
  useEffect(() => {
    if (!runId) return;
    if (runId === "demo") {
      setLayout(MOCK_LAYOUT);
      return;
    }
    if (runLayout) {
      setLayout(wireToLayout(runLayout));
      return;
    }
    if (runLayoutError) {
      console.warn(
        "[layout] fetch failed, falling back to mock:",
        runLayoutError,
      );
      setLayout(MOCK_LAYOUT);
    }
  }, [runId, runLayout, runLayoutError]);

  const monoFont =
    'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

  const backLink = projectId
    ? `/projects/${projectId}?tab=studies`
    : "/projects";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
        background: "#ffffff",
      }}
    >
      <header
        style={{
          flex: "0 0 auto",
          display: "flex",
          flexDirection: "column",
          gap: "0.2rem",
          padding: "0.65rem 1.25rem",
          borderBottom: "1px solid #E3E8EE",
          background: "#ffffff",
          fontSize: "0.875rem",
        }}
      >
        {/* Top row: parent-chain breadcrumb (Project › Study). */}
        <nav
          aria-label="Breadcrumb"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            fontSize: "0.75rem",
            color: "#8898aa",
          }}
        >
          {/* Plain <a> (full page load) so leaving the viewer unloads and
              frees the ODA WASM engine — no singleton carries over. */}
          {projectId ? (
            <a
              href={`/projects/${projectId}`}
              data-testid="breadcrumb-project"
              style={{ color: "#8898aa", textDecoration: "none" }}
            >
              {projectName ?? "Project"}
            </a>
          ) : (
            <span>Project</span>
          )}
          <span aria-hidden="true">›</span>
          {projectId ? (
            <a
              href={`/projects/${projectId}?tab=studies`}
              data-testid="breadcrumb-study"
              style={{ color: "#8898aa", textDecoration: "none" }}
            >
              {studyName ?? "Study"}
            </a>
          ) : (
            <span>{studyName ?? "Study"}</span>
          )}
        </nav>

        {/* Bottom row: back-link on the left, current location on the right. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
          }}
        >
          <a
            href={backLink}
            data-testid="back-to-studies"
            style={{
              color: "#2563eb",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            ← Back to Studies
          </a>
          <span
            data-testid="layout-context-label"
            style={{ color: "#0a0e27", fontWeight: 500 }}
          >
            {[projectName, studyName, runLabel].filter(Boolean).join(" / ") ||
              (runId ? `run · ${runId}` : "")}
          </span>
        </div>
      </header>

      <div style={{ position: "relative", flex: "1 1 auto", minHeight: 0 }}>
        {!layout ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              color: "#6b7280",
              fontSize: 13,
            }}
          >
            Loading layout…
          </div>
        ) : (
          <>
            <ODAViewer
              templateUrl="/static/cad/template_splantt_off.dwg"
              layout={layout}
              deleteRequestId={deleteRequestId}
              regenRequestId={regenRequestId}
              saveRequestId={saveRequestId}
              runId={runId}
            />
            <div
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                display: "flex",
                flexDirection: "column",
                gap: 8,
                pointerEvents: "none",
              }}
            >
              <button
                type="button"
                onClick={() => setDeleteRequestId((n) => n + 1)}
                style={{
                  pointerEvents: "auto",
                  padding: "8px 12px",
                  borderRadius: 6,
                  background: "#dc2626",
                  color: "#ffffff",
                  border: "1px solid #b91c1c",
                  fontFamily: monoFont,
                  fontSize: 13,
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  userSelect: "none",
                }}
              >
                🗑 Delete Selected
              </button>
              <button
                type="button"
                onClick={() => setRegenRequestId((n) => n + 1)}
                title="Force the viewer to re-render (regen) from the current model — no reload"
                style={{
                  pointerEvents: "auto",
                  padding: "8px 12px",
                  borderRadius: 6,
                  background: "#0ea5e9",
                  color: "#ffffff",
                  border: "1px solid #0284c7",
                  fontFamily: monoFont,
                  fontSize: 13,
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  userSelect: "none",
                }}
              >
                🔄 Regen View
              </button>
              <button
                type="button"
                disabled={isSaving}
                onClick={() => {
                  setIsSaving(true);
                  setSaveRequestId((n) => n + 1);
                  // db.writeFile + byte-by-byte streambuf drain is ~1-3s
                  // for a typical DWG. The timeout caps the disabled
                  // state generously so the user can re-click after a
                  // failure without waiting on a missing completion signal.
                  setTimeout(() => setIsSaving(false), 3500);
                }}
                style={{
                  pointerEvents: "auto",
                  padding: "8px 12px",
                  borderRadius: 6,
                  background: isSaving ? "#e5e7eb" : "#ffffff",
                  color: "#111827",
                  border: "1px solid #e5e7eb",
                  fontFamily: monoFont,
                  fontSize: 13,
                  cursor: isSaving ? "wait" : "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  userSelect: "none",
                  opacity: isSaving ? 0.7 : 1,
                }}
              >
                {isSaving ? "⏳ Saving... (~1-3s)" : "💾 Save as DWG"}
              </button>
              <div
                style={{
                  background: "rgba(255, 255, 255, 0.92)",
                  border: "1px solid #e5e7eb",
                  borderRadius: 6,
                  padding: "10px 14px",
                  fontFamily: monoFont,
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: "#111827",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  userSelect: "none",
                }}
              >
                {(() => {
                  // Stage L.4.d: count derives from cached output_data,
                  // which useEditOps mutates optimistically. Falls back
                  // to the static layout (used by /demo) when there's
                  // no server-backed cache.
                  const count =
                    runLayout?.trackers.length ?? layout.trackers.length;
                  const modulesPerTracker =
                    layout.packing_config.modules_per_tracker;
                  // Stage 11 Step 0 — packing report (n_blocks, fill_ratio,
                  // chosen skew, runtime). Present only for ac_block_layout
                  // runs that were packed under Stage 11+ code.
                  const report =
                    runLayout?.packing_report ?? layout.packing_report;
                  return (
                    <>
                      <div>Trackers: {count}</div>
                      <div>
                        Modules: {count} × {modulesPerTracker} ={" "}
                        {(count * modulesPerTracker).toLocaleString()}
                      </div>
                      {report && (
                        <>
                          <div
                            aria-hidden="true"
                            style={{
                              borderTop: "1px solid #e5e7eb",
                              margin: "6px 0",
                            }}
                          />
                          <div>AC blocks: {report.n_blocks}</div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
